import { paramCase } from 'src/utils/change-case';

const MOCK_ID = 'mock-id';
const MOCK_TITLE = 'mock-title';

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
};

// ----------------------------------------------------------------------

export const paths = {
  home: '/dashboard/projects',
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/error/403',
  page404: '/error/404',
  page500: '/error/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneStore: 'https://mui.com/store/items/zone-landing-page/',
  minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figma: 'https://www.figma.com/design/cAPz4pYPtQEXivqe11EcDE/%5BPreview%5D-Minimal-Web.v6.0.0',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id: string) => `/product/${id}`,
    demo: { details: `/product/${MOCK_ID}` },
  },
  standard: {
    root: `/standard`,
    checkout: `/standard/checkout`,
    details: (id: string) => `/standard/${id}`,
    demo: { details: `/standard/${MOCK_ID}` },
  },
  post: {
    root: `/post`,
    details: (title: string) => `/post/${paramCase(title)}`,
    demo: { details: `/post/${paramCase(MOCK_TITLE)}` },
  },
  // AUTH
  auth: {
    amplify: {
      signIn: `${ROOTS.AUTH}/amplify/sign-in`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      signUp: `${ROOTS.AUTH}/amplify/sign-up`,
      updatePassword: `${ROOTS.AUTH}/amplify/update-password`,
      resetPassword: `${ROOTS.AUTH}/amplify/reset-password`,
    },
    jwt: {
      signIn: `${ROOTS.AUTH}/jwt/sign-in`,
      signUp: `${ROOTS.AUTH}/jwt/sign-up`,
      verify: `${ROOTS.AUTH}/jwt/verify`,
      verifyExternal: `${ROOTS.AUTH}/jwt/verify-external`,
      verifyEmailPending: `${ROOTS.AUTH}/jwt/verify-email-pending`,
      updatePassword: `${ROOTS.AUTH}/jwt/update-password`,
      resetPassword: `${ROOTS.AUTH}/jwt/reset-password`,
      mfaEnrollment: `${ROOTS.AUTH}/jwt/mfa-enrollment`,
      ssoCallback: `${ROOTS.AUTH}/jwt/sso-callback`,
    },
    firebase: {
      signIn: `${ROOTS.AUTH}/firebase/sign-in`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      signUp: `${ROOTS.AUTH}/firebase/sign-up`,
      resetPassword: `${ROOTS.AUTH}/firebase/reset-password`,
    },
    auth0: {
      signIn: `${ROOTS.AUTH}/auth0/sign-in`,
    },
    supabase: {
      signIn: `${ROOTS.AUTH}/supabase/sign-in`,
      verify: `${ROOTS.AUTH}/supabase/verify`,
      signUp: `${ROOTS.AUTH}/supabase/sign-up`,
      updatePassword: `${ROOTS.AUTH}/supabase/update-password`,
      resetPassword: `${ROOTS.AUTH}/supabase/reset-password`,
    },
  },

  // DASHBOARD
  dashboard: {
    root: '/dashboard',
    mail: `${ROOTS.DASHBOARD}/mail`,
    chat: `${ROOTS.DASHBOARD}/chat`,
    blank: `${ROOTS.DASHBOARD}/blank`,
    kanban: `${ROOTS.DASHBOARD}/kanban`,
    calendar: `${ROOTS.DASHBOARD}/calendar`,
    fileManager: `${ROOTS.DASHBOARD}/file-manager`,
    permission: `${ROOTS.DASHBOARD}/permission`,
    account: `${ROOTS.DASHBOARD}/account`,
    schedule: {
      root: `${ROOTS.DASHBOARD}/schedule`,
    },
    leave: {
      root: `${ROOTS.DASHBOARD}/leave`,
    },
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
      course: `${ROOTS.DASHBOARD}/course`,
    },
    reports: {
      root: `${ROOTS.DASHBOARD}/reports`,
    },

    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    product: {
      root: `${ROOTS.DASHBOARD}/product`,
      new: `${ROOTS.DASHBOARD}/product/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/product/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },
    standard: {
      root: `${ROOTS.DASHBOARD}/standards`,
      new: `${ROOTS.DASHBOARD}/standards/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/standards/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/standards/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/standards/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/standards/${MOCK_ID}/edit`,
      },
    },
    competency: {
      root: `${ROOTS.DASHBOARD}/competencies`,
      new: `${ROOTS.DASHBOARD}/competencies/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/competencies/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/competencies/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/competencies/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/competencies/${MOCK_ID}/edit`,
      },
    },
    'service-types': {
      root: `${ROOTS.DASHBOARD}/service-types`,
      new: `${ROOTS.DASHBOARD}/service-types/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/service-types/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/service-types/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/service-types/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/service-types/${MOCK_ID}/edit`,
      },
    },
    client: {
      root: `${ROOTS.DASHBOARD}/clients`,
      new: `${ROOTS.DASHBOARD}/clients/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/clients/${id}`,
      /** Customer details with Sites tab selected (hash sync in CustomerDetailsView). */
      detailsSitesTab: (id: string | number) => `${ROOTS.DASHBOARD}/clients/${id}#sites`,
      /** Customer details with Contact People tab selected (hash sync in CustomerDetailsView). */
      detailsContactPeopleTab: (id: string | number) =>
        `${ROOTS.DASHBOARD}/clients/${id}#contactPeople`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/clients/${id}/edit`,
      categories: `${ROOTS.DASHBOARD}/clients/categories`,
      demo: {
        details: `${ROOTS.DASHBOARD}/clients/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/clients/${MOCK_ID}/edit`,
      },
    },
    site: {
      root: `${ROOTS.DASHBOARD}/site`,
      new: `${ROOTS.DASHBOARD}/site/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/site/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/site/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/site/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/site/${MOCK_ID}/edit`,
      },
    },
    visit: {
      root: `${ROOTS.DASHBOARD}/activities`,
      new: `${ROOTS.DASHBOARD}/activities/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/activities/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/activities/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/activities/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/activities/${MOCK_ID}/edit`,
      },
    },
    team: {
      root: `${ROOTS.DASHBOARD}/team`,
      new: `${ROOTS.DASHBOARD}/team/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/team/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/team/${id}/edit`,
      fileImport: `${ROOTS.DASHBOARD}/team/file-import`,
    },
    recurringschedules: {
      root: `${ROOTS.DASHBOARD}/recurringschedules`,
      new: `${ROOTS.DASHBOARD}/recurringschedules/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/recurringschedules/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/recurringschedules/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/recurringschedules/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/recurringschedules/${MOCK_ID}/edit`,
      },
    },
    solutions: {
      root: `${ROOTS.DASHBOARD}/solutions`,
      details: (id: string) => `${ROOTS.DASHBOARD}/solutions/${id}`,
    },
    projects: {
      root: `${ROOTS.DASHBOARD}/projects`,
      new: `${ROOTS.DASHBOARD}/projects/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/projects/${id}`,
      auditTrails: (id: string) => `${ROOTS.DASHBOARD}/projects/${id}#audit-trails`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/projects/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/projects/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/projects/${MOCK_ID}/edit`,
      },
    },
    programmes: {
      root: `${ROOTS.DASHBOARD}/programmes`,
      new: `${ROOTS.DASHBOARD}/programmes/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/programmes/${id}`,
      /** Static route so the wizard is exported once; the programme comes from `?id=`. */
      editRoot: `${ROOTS.DASHBOARD}/programmes/edit`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/programmes/edit?id=${id}`,
    },

    createCompanyName: {
      root: `/createCompanyName`,
    },

    invoice: {
      root: `${ROOTS.DASHBOARD}/invoice`,
      new: `${ROOTS.DASHBOARD}/invoice/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.DASHBOARD}/post`,
      new: `${ROOTS.DASHBOARD}/post/new`,
      details: (title: string) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}`,
      edit: (title: string) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.DASHBOARD}/order`,
      details: (id: string) => `${ROOTS.DASHBOARD}/order/${id}`,
      demo: {
        details: `${ROOTS.DASHBOARD}/order/${MOCK_ID}`,
      },
    },
    job: {
      root: `${ROOTS.DASHBOARD}/job`,
      new: `${ROOTS.DASHBOARD}/job/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/job/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/job/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.DASHBOARD}/tour`,
      new: `${ROOTS.DASHBOARD}/tour/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
    region: {
      root: `${ROOTS.DASHBOARD}/region`,
    },
    configuration: {
      root: `${ROOTS.DASHBOARD}/configuration`,
    },
    member: {
      root: `${ROOTS.DASHBOARD}/member`,
      new: `${ROOTS.DASHBOARD}/member/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/member/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/member/${id}/edit`,
      holidays: (id: string) => `${ROOTS.DASHBOARD}/member/${id}/holidays`,
      profile: `${ROOTS.DASHBOARD}/member/profile`,
    },
    holidays: {
      root: '/dashboard/holidays',
      new: '/dashboard/holidays/new',
      edit: (id: string) => `/dashboard/holidays/${id}/edit`,
      details: (id: string) => `/dashboard/holidays/${id}`,
    },
    roles: {
      root: `${ROOTS.DASHBOARD}/roles`,
    },
    competencyCategories: {
      root: `${ROOTS.DASHBOARD}/competency-categories`,
    },
    clientTypes: {
      root: `${ROOTS.DASHBOARD}/client-types`,
    },
    standardCategories: {
      root: `${ROOTS.DASHBOARD}/standard-categories`,
    },
    accreditationBodies: {
      root: `${ROOTS.DASHBOARD}/accreditation-bodies`,
    },
    fileImport: {
      root: `${ROOTS.DASHBOARD}/file-import`,
    },
    organization: {
      root: `${ROOTS.DASHBOARD}/organization`,
      users: `${ROOTS.DASHBOARD}/organization/users`,
      userEdit: (id: string) => `${ROOTS.DASHBOARD}/organization/users/${id}/edit`,
      profile: (id: string) => `${ROOTS.DASHBOARD}/organization/users/${id}/profile`,
      settings: `${ROOTS.DASHBOARD}/organization/settings`,
      fileImport: `${ROOTS.DASHBOARD}/organization/file-import`,
      configuration: `${ROOTS.DASHBOARD}/organization/configuration`,
      configurationSso: `${ROOTS.DASHBOARD}/organization/configuration/sso`,
      configurationEmailOutbox: `${ROOTS.DASHBOARD}/organization/configuration/email-outbox`,
      configurationMicrosoft365: `${ROOTS.DASHBOARD}/organization/configuration/microsoft365`,
      auditTrails: `${ROOTS.DASHBOARD}/organization/audit-trails`,
      webhookLogs: `${ROOTS.DASHBOARD}/organization/webhook-logs`,
      organizationalRoles: `${ROOTS.DASHBOARD}/organization/organizational-roles`,
      emailTemplates: `${ROOTS.DASHBOARD}/organization/email-templates`,
      emailConfiguration: `${ROOTS.DASHBOARD}/organization/email-configuration`,
      businessUnits: `${ROOTS.DASHBOARD}/organization/business-units`,
    },
    tasks: {
      root: `${ROOTS.DASHBOARD}/tasks`,
    },
  },
};
